import { FaroTextFieldMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { LabelWithHelp } from "@components/common/label-with-help";
import { createMemberOption, createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { Stack } from "@mui-wrapper";
import { addGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { useMemo, useState } from "react";
import { Dialog, TextField, Button } from "@flat-ui";
import { STACK_ERROR_SX } from "@components/common/dialog/dialog-constants";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useTeamUtils } from "@hooks/use-team-utils";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { sortTeams } from "@utils/team-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
/**
 * Renders the create group button and the dialog to be used to create new groups.
 * This component does not take care of the authorization of the user to create groups.
 */
export function CreateGroup({
  groups
}) {
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const companyId = useCompanyId();
  const companyMembers = useCompanyMembers();
  const currentUser = useAppSelector(currentUserSelector);
  const teams = useAppSelector(assignableTeamsSelector);
  const {
    isTeamName
  } = useTeamUtils();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /** Stores the name for the group to be created */
  const [groupName, setGroupName] = useState("");

  /** Stores the names of the groups the user has access to. */
  const groupNames = groups.map(group => group.name);

  /** Stores the members of the company in the format required by the autocomplete */
  const memberOptions = useMemo(() => {
    return sortMembersByNameOrEmail(companyMembers).map(member => {
      /**
       * If the user is a group manager, it should be added to the group by default
       * and it should not be removed, otherwise the creator of the group would not have access to it.
       */
      const isPreselected = currentUser?.role === "COMPANY_MANAGER" && currentUser?.identity === member.identity;
      return createMemberOption({
        member,
        isPreselected
      });
    });
  }, [companyMembers, currentUser]);

  /** Reforming team data for autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => createTeamOption({
    team
  })), [teams]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers: selectedManagers,
    setSelectedMembers: setSelectedManagers,
    uniqueSelectedValues: uniqueManagersIds,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "group",
    existingMembers: [],
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_0 => member_0.identity)
  });

  /** Stores whether the create button should be disabled */
  const isCreateDisabled = useMemo(() => {
    // Workspaces without the manager feature only pick a group name, so the members
    // field is neither shown nor validated. The current user becomes the implicit
    // manager, so without them there is no manager to send.
    if (!canAssignManagers) {
      return !groupName || !currentUser;
    }
    return !groupName || shouldDisableSendInvite(uniqueManagersIds, isMemberInputValid);
  }, [canAssignManagers, currentUser, groupName, uniqueManagersIds, isMemberInputValid]);

  /** Resets the dialog content, and opens the dialog. */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog() {
    // Add all the preselected members as group managers by default.
    const preselectedManagers = memberOptions.filter(member_1 => member_1.isPreselected);
    setSelectedManagers(preselectedManagers);
    setIsDialogOpen(true);
  }
  function closeDialog() {
    reset();
    setGroupName("");
    setIsDialogOpen(false);
  }

  /** Triggered when the create button is clicked, creates the group and closes the dialog */
  async function handleConfirm() {
    setIsLoading(true);
    try {
      // When the workspace cannot assign managers the manager field is hidden and the
      // current user (main admin) is used implicitly, since the backend requires at least
      // one group manager. `isCreateDisabled` guarantees the current user is loaded there.
      const selectedGroupManagerIds = canAssignManagers || !currentUser ? uniqueManagersIds : [currentUser.identity];
      await dispatch(addGroup({
        companyId,
        groupName,
        selectedGroupManagerIds
      })).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `createGroup-${Date.now().toString()}`,
        title: "Error creating group",
        error
      });
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }
  return <>
      {/* Button to open the dialog */}
      <Button onClick={onOpenDialog} sx={{
      width: isScreenSmOrSmaller ? "100%" : "auto"
    }}>
        New group
      </Button>

      {/* Create group dialog */}
      <Dialog title="New group" open={isDialogOpen} confirmText="Create" isConfirmDisabled={isCreateDisabled} onConfirm={handleConfirm} showSpinner={isLoading} onCancel={closeDialog}>
        {/* Group name */}
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group name" isRequired />
          <TextField autoFocus placeholder="Enter group name" fullWidth text={groupName} onTextChanged={setGroupName} />
          <FaroTextFieldMessage shouldHide={!groupNames.includes(groupName)} message={{
          type: "info",
          helperText: "Group name already exists"
        }} />
        </Stack>

        {/* Groups manager - only workspaces that can assign managers pick members here */}
        {canAssignManagers && <Stack sx={STACK_ERROR_SX}>
            <MembersAutocomplete isRequired label="Group manager" helperText={helperText} options={autocompleteOptions} onInputChange={onInputChange} selectedOptions={selectedManagers} handleMemberSelect={handleMemberSelect} isValid={value => isNewEmailValid(value) || isTeamName(value)} />
          </Stack>}
      </Dialog>
    </>;
}